import { useState, useRef } from "react";
import { useUser } from "../../../contexts/UserContext";
import ImageCropperModal from "../../../components/base/ImageCropperModal";
import { fileToDataUrl } from "../../../utils/cropImage";

export default function ProfileEditSection() {
  const { user, updateProfile, updateUsername, updateEmail, updatePassword, uploadAvatar, uploadBanner } = useUser();
  const fileRef = useRef<HTMLInputElement>(null);
  const bannerRef = useRef<HTMLInputElement>(null);

  const [tab, setTab] = useState<"info" | "email" | "password">("info");

  // Cropper state
  const [cropperSrc, setCropperSrc] = useState<string | null>(null);
  const [cropperType, setCropperType] = useState<"avatar" | "banner">("avatar");

  // Info form
  const [displayName, setDisplayName] = useState(user?.displayName ?? "");
  const [phone, setPhone] = useState(user?.phone ?? "");
  const [bio, setBio] = useState(user?.bio ?? "");
  const [infoLoading, setInfoLoading] = useState(false);
  const [infoMsg, setInfoMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Username form
  const [newUsername, setNewUsername] = useState(user?.username ?? "");
  const [usernameLoading, setUsernameLoading] = useState(false);
  const [usernameMsg, setUsernameMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Email form
  const [newEmail, setNewEmail] = useState("");
  const [emailPass, setEmailPass] = useState("");
  const [emailLoading, setEmailLoading] = useState(false);
  const [emailMsg, setEmailMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Password form
  const [curPass, setCurPass] = useState("");
  const [newPass, setNewPass] = useState("");
  const [newPass2, setNewPass2] = useState("");
  const [passLoading, setPassLoading] = useState(false);
  const [passMsg, setPassMsg] = useState<{ ok: boolean; text: string } | null>(null);

  // Avatar
  const [avatarLoading, setAvatarLoading] = useState(false);
  const [avatarError, setAvatarError] = useState("");

  // Banner
  const [bannerLoading, setBannerLoading] = useState(false);
  const [bannerError, setBannerError] = useState("");
  const [bannerMsg, setBannerMsg] = useState("");

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setAvatarError("");
    const src = await fileToDataUrl(file);
    setCropperType("avatar");
    setCropperSrc(src);
    e.target.value = "";
  };

  const handleBannerChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBannerError("");
    setBannerMsg("");
    const src = await fileToDataUrl(file);
    setCropperType("banner");
    setCropperSrc(src);
    e.target.value = "";
  };

  const handleCropConfirm = async (blob: Blob) => {
    setCropperSrc(null);
    const file = new File([blob], `${cropperType}.jpg`, { type: "image/jpeg" });

    if (cropperType === "avatar") {
      setAvatarLoading(true);
      const res = await uploadAvatar(file);
      if (!res.success) setAvatarError(res.error ?? "Yükleme başarısız.");
      setAvatarLoading(false);
    } else {
      setBannerLoading(true);
      const res = await uploadBanner(file);
      if (!res.success) {
        setBannerError(res.error ?? "Banner yükleme başarısız.");
      } else {
        setBannerMsg("Banner güncellendi!");
      }
      setBannerLoading(false);
    }
  };

  const handleRemoveBanner = async () => {
    setBannerError("");
    setBannerMsg("");
    const res = await updateProfile({ bannerUrl: "" });
    if (res.success) setBannerMsg("Banner kaldırıldı.");
    else setBannerError(res.error ?? "Hata oluştu.");
  };

  const handleInfoSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!displayName.trim() || displayName.trim().length < 2) {
      setInfoMsg({ ok: false, text: "Adınız en az 2 karakter olmalıdır." });
      return;
    }
    setInfoLoading(true);
    setInfoMsg(null);
    const res = await updateProfile({ displayName: displayName.trim(), phone: phone.trim(), bio: bio.trim() });
    setInfoMsg(
      res.success
        ? { ok: true, text: "Bilgiler güncellendi!" }
        : { ok: false, text: res.error ?? "Hata oluştu." },
    );
    setInfoLoading(false);
  };

  const handleUsernameSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setUsernameLoading(true);
    setUsernameMsg(null);
    const res = await updateUsername(newUsername);
    if (res.success) {
      setNewUsername(newUsername.trim().toLowerCase().replace(/[^a-z0-9_]/g, ""));
    }
    setUsernameMsg(
      res.success
        ? { ok: true, text: "Kullanıcı adınız güncellendi!" }
        : { ok: false, text: res.error ?? "Hata oluştu." },
    );
    setUsernameLoading(false);
  };

  const handleEmailSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setEmailLoading(true);
    setEmailMsg(null);
    const res = await updateEmail(newEmail.trim(), emailPass);
    setEmailMsg(
      res.success
        ? { ok: true, text: "E-posta güncellendi! Yeni adresinize bir onay maili gönderildi." }
        : { ok: false, text: res.error ?? "Hata oluştu." },
    );
    if (res.success) { setNewEmail(""); setEmailPass(""); }
    setEmailLoading(false);
  };

  const handlePasswordSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (newPass !== newPass2) {
      setPassMsg({ ok: false, text: "Yeni şifreler eşleşmiyor." });
      return;
    }
    if (newPass.length < 6) {
      setPassMsg({ ok: false, text: "Şifre en az 6 karakter olmalı." });
      return;
    }
    setPassLoading(true);
    setPassMsg(null);
    const res = await updatePassword(curPass, newPass);
    setPassMsg(
      res.success
        ? { ok: true, text: "Şifre başarıyla güncellendi!" }
        : { ok: false, text: res.error ?? "Hata oluştu." },
    );
    if (res.success) { setCurPass(""); setNewPass(""); setNewPass2(""); }
    setPassLoading(false);
  };

  const inputCls =
    "w-full border border-stone-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:border-emerald-400 transition-colors";
  const msgCls = (ok: boolean) =>
    `text-sm px-4 py-3 rounded-xl border flex items-start gap-2 ${
      ok
        ? "bg-emerald-50 text-emerald-700 border-emerald-200"
        : "bg-red-50 text-red-600 border-red-200"
    }`;

  const initials = (user?.displayName || user?.username || "K").charAt(0).toUpperCase();

  return (
    <div className="space-y-6">
      {/* Image Cropper Modal */}
      {cropperSrc && (
        <ImageCropperModal
          imageSrc={cropperSrc}
          aspect={cropperType === "avatar" ? 1 : 3}
          cropShape={cropperType === "avatar" ? "round" : "rect"}
          title={cropperType === "avatar" ? "Profil Fotoğrafı Kırp" : "Banner Kırp"}
          onConfirm={handleCropConfirm}
          onClose={() => setCropperSrc(null)}
        />
      )}

      {/* Banner */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div
          className="w-full h-28 relative group"
          style={{
            background: user?.bannerUrl
              ? undefined
              : "linear-gradient(135deg, #064e3b 0%, #059669 50%, #57534e 100%)",
          }}
        >
          {user?.bannerUrl ? (
            <img
              src={user.bannerUrl}
              alt="Banner"
              className="w-full h-full object-cover object-top"
            />
          ) : (
            <div
              className="absolute inset-0 opacity-10"
              style={{ backgroundImage: "radial-gradient(circle, white 1px, transparent 1px)", backgroundSize: "24px 24px" }}
            />
          )}
          <div className="absolute inset-0 flex items-center justify-center gap-3 opacity-0 group-hover:opacity-100 transition-opacity bg-black/25">
            <button
              onClick={() => bannerRef.current?.click()}
              disabled={bannerLoading}
              className="flex items-center gap-1.5 px-4 py-2 bg-white/90 hover:bg-white rounded-xl text-stone-800 text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
            >
              {bannerLoading ? <i className="ri-loader-4-line animate-spin" /> : <i className="ri-upload-cloud-line" />}
              {bannerLoading ? "Yükleniyor..." : "Fotoğraf Yükle"}
            </button>
            {user?.bannerUrl && (
              <button
                onClick={handleRemoveBanner}
                className="flex items-center gap-1.5 px-4 py-2 bg-red-500 hover:bg-red-600 rounded-xl text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              >
                <i className="ri-delete-bin-line" />
                Kaldır
              </button>
            )}
          </div>
        </div>

        <div className="px-6 py-4">
          <div className="flex items-center justify-between mb-1">
            <h3 className="font-bold text-stone-900">Profil Kapak Fotoğrafı</h3>
            <button
              onClick={() => bannerRef.current?.click()}
              disabled={bannerLoading}
              className="flex items-center gap-1.5 px-3 py-2 border border-stone-200 rounded-xl text-xs font-semibold text-stone-700 hover:bg-stone-50 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60"
            >
              <i className="ri-image-edit-line" />
              Değiştir
            </button>
          </div>
          <p className="text-xs text-stone-400">JPG, PNG, WEBP · Maks. 5MB · Önerilen oran 3:1 (örn. 1200×400)</p>
          <input
            ref={bannerRef}
            type="file"
            accept="image/jpeg,image/png,image/webp"
            className="hidden"
            onChange={handleBannerChange}
          />
          {bannerError && (
            <p className="text-xs text-red-500 mt-2 flex items-center gap-1"><i className="ri-error-warning-line" />{bannerError}</p>
          )}
          {bannerMsg && (
            <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1"><i className="ri-checkbox-circle-line" />{bannerMsg}</p>
          )}
        </div>
      </div>

      {/* Avatar */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <h3 className="font-bold text-stone-900 mb-4">Profil Fotoğrafı</h3>
        <div className="flex items-center gap-5">
          <div className="relative flex-shrink-0">
            {user?.avatarUrl ? (
              <img
                src={user.avatarUrl}
                alt={user.displayName}
                className="w-20 h-20 rounded-2xl object-cover object-top"
              />
            ) : (
              <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-500 to-emerald-800 flex items-center justify-center">
                <span className="text-white font-bold text-3xl">{initials}</span>
              </div>
            )}
            {avatarLoading && (
              <div className="absolute inset-0 bg-black/40 rounded-2xl flex items-center justify-center">
                <i className="ri-loader-4-line text-white text-xl animate-spin" />
              </div>
            )}
          </div>
          <div>
            <input
              ref={fileRef}
              type="file"
              accept="image/jpeg,image/png,image/webp,image/gif"
              className="hidden"
              onChange={handleAvatarChange}
            />
            <button
              onClick={() => fileRef.current?.click()}
              disabled={avatarLoading}
              className="px-4 py-2.5 border border-stone-200 rounded-xl text-sm font-semibold text-stone-700 hover:bg-stone-50 cursor-pointer transition-colors whitespace-nowrap disabled:opacity-60"
            >
              <i className="ri-upload-line mr-1.5" />
              Fotoğraf Yükle
            </button>
            <p className="text-xs text-stone-400 mt-2">JPG, PNG, WEBP, GIF · Maks. 2MB</p>
            {avatarError && <p className="text-xs text-red-500 mt-1">{avatarError}</p>}
          </div>
        </div>

        {/* Profile URL */}
        <div className="mt-4 pt-4 border-t border-stone-100 flex items-center gap-2">
          <i className="ri-link text-stone-400 text-sm" />
          <span className="text-xs text-stone-500">Profil URL:</span>
          <a
            href={`/profile/${user?.username}`}
            className="text-xs text-emerald-700 hover:underline font-medium whitespace-nowrap"
          >
            /profile/{user?.username}
          </a>
        </div>
      </div>

      {/* Username Card */}
      <div className="bg-white rounded-2xl border border-stone-200 p-6">
        <h3 className="font-bold text-stone-900 mb-1">Kullanıcı Adı</h3>
        <p className="text-xs text-stone-400 mb-4">Diğer kullanıcılar sizi bu adla bulur. Sadece harf, rakam ve alt çizgi (_).</p>
        <form onSubmit={handleUsernameSave} className="flex gap-3 items-start">
          <div className="flex-1">
            <div className="flex items-center border border-stone-200 rounded-xl overflow-hidden focus-within:border-emerald-400 transition-colors">
              <span className="px-3 py-3 text-stone-400 text-sm bg-stone-50 border-r border-stone-200 flex-shrink-0">@</span>
              <input
                value={newUsername}
                onChange={(e) => setNewUsername(e.target.value.toLowerCase().replace(/[^a-z0-9_]/g, ""))}
                placeholder="kullanici_adi"
                minLength={3}
                maxLength={30}
                required
                className="flex-1 px-3 py-3 text-sm focus:outline-none bg-white"
              />
            </div>
            {usernameMsg && (
              <div className={`mt-2 text-xs px-3 py-2 rounded-lg border flex items-center gap-1.5 ${usernameMsg.ok ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-red-50 text-red-600 border-red-200"}`}>
                <i className={usernameMsg.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"} />
                {usernameMsg.text}
              </div>
            )}
          </div>
          <button
            type="submit"
            disabled={usernameLoading || newUsername === user?.username}
            className="px-5 py-3 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-50 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors flex-shrink-0"
          >
            {usernameLoading ? <i className="ri-loader-4-line animate-spin" /> : "Kaydet"}
          </button>
        </form>
      </div>

      {/* Settings Tabs */}
      <div className="bg-white rounded-2xl border border-stone-200 overflow-hidden">
        <div className="flex border-b border-stone-100">
          {(
            [
              ["info", "Bilgiler"],
              ["email", "E-posta"],
              ["password", "Şifre"],
            ] as const
          ).map(([key, label]) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`flex-1 py-3 text-sm font-semibold cursor-pointer transition-colors whitespace-nowrap ${
                tab === key
                  ? "border-b-2 border-emerald-600 text-emerald-700 bg-emerald-50/40"
                  : "text-stone-500 hover:text-stone-700"
              }`}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="p-6">
          {tab === "info" && (
            <form onSubmit={handleInfoSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Adınız</label>
                <input
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  placeholder="Adınız Soyadınız"
                  required
                  className={inputCls}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  Bio <span className="text-stone-400 font-normal text-xs">(profil sayfanızda görünür)</span>
                </label>
                <textarea
                  value={bio}
                  onChange={(e) => setBio(e.target.value.slice(0, 160))}
                  placeholder="Kendinizden kısaca bahsedin..."
                  rows={3}
                  className={`${inputCls} resize-none`}
                />
                <p className="text-xs text-stone-400 mt-1 text-right">{bio.length}/160</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">
                  Telefon <span className="text-stone-400 font-normal text-xs">(isteğe bağlı)</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-stone-400 text-sm">+90</span>
                  <input
                    type="tel"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))}
                    placeholder="5XX XXX XX XX"
                    className={`${inputCls} pl-12`}
                  />
                </div>
              </div>
              {infoMsg && (
                <div className={msgCls(infoMsg.ok)}>
                  <i className={`flex-shrink-0 text-sm mt-0.5 ${infoMsg.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"}`} />
                  {infoMsg.text}
                </div>
              )}
              <button
                type="submit"
                disabled={infoLoading}
                className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors"
              >
                {infoLoading ? "Kaydediliyor..." : "Kaydet"}
              </button>
            </form>
          )}

          {tab === "email" && (
            <form onSubmit={handleEmailSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1">Mevcut E-posta</label>
                <p className="text-sm text-stone-500 bg-stone-50 rounded-xl px-4 py-3">{user?.email}</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Yeni E-posta</label>
                <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Mevcut Şifreniz</label>
                <input type="password" value={emailPass} onChange={(e) => setEmailPass(e.target.value)} required className={inputCls} />
              </div>
              {emailMsg && (
                <div className={msgCls(emailMsg.ok)}>
                  <i className={`flex-shrink-0 text-sm mt-0.5 ${emailMsg.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"}`} />
                  {emailMsg.text}
                </div>
              )}
              <button type="submit" disabled={emailLoading} className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors">
                {emailLoading ? "Güncelleniyor..." : "E-postayı Güncelle"}
              </button>
            </form>
          )}

          {tab === "password" && (
            <form onSubmit={handlePasswordSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Mevcut Şifre</label>
                <input type="password" value={curPass} onChange={(e) => setCurPass(e.target.value)} required className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Yeni Şifre</label>
                <input type="password" value={newPass} onChange={(e) => setNewPass(e.target.value)} required minLength={6} className={inputCls} />
              </div>
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Yeni Şifre (Tekrar)</label>
                <input type="password" value={newPass2} onChange={(e) => setNewPass2(e.target.value)} required className={inputCls} />
              </div>
              {passMsg && (
                <div className={msgCls(passMsg.ok)}>
                  <i className={`flex-shrink-0 text-sm mt-0.5 ${passMsg.ok ? "ri-checkbox-circle-line" : "ri-error-warning-line"}`} />
                  {passMsg.text}
                </div>
              )}
              <button type="submit" disabled={passLoading} className="px-6 py-2.5 bg-emerald-700 hover:bg-emerald-800 disabled:opacity-60 text-white rounded-xl text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors">
                {passLoading ? "Güncelleniyor..." : "Şifreyi Güncelle"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
